<template>
  <div class="dvrPlayer">
    <div class="playerContainer">
<!--      <div class="playerContainer__fallback fallback-poster">-->
<!--        <img :src="poster" class="fallback-poster__image" />-->
<!--      </div>-->
      <!-- Channel player -->
      <segment-player
        ref="segmentPlayer"
        :link="ndvrStream"
        :time.sync="timeProp"
        :paused.sync="pausedProp"
        :muted.sync="sound.muted"
        :max-time="streamEnd"
        :min-time="streamStart"
        :volume="actualVolume"
        :poster="poster"
        :mode="playerMode"
        class="videoPlayerTag editorPlayer"
        @playerRendered="player = $event"
        @updateTime="updateTime"
        @playing="playBlocked = true"
        @played="playBlocked = false"
        @loaded="$emit('streamLoaded')"
        @imageEmptied="playerLoading = true"
        @imageLoaded="playerLoading = false"
        @playingLive="onLive = true"
        @playingDvr="onLive = false"
      />
      <!-- Controls -->
      <player-controls
        class="player-controls"
        :class="{ 'player-controls_visible_always': !hasVideo }"
        :time="time"
        :timezone="timezone"
        :volume="actualVolume"
        :muted="sound.muted"
        :is-snapshot-enabled="hasVideo && !playerLoading"
        @openTimezone="openTimezoneModal"
        @changeVolume="setVolume"
        @mute="toggleMutePlayer"
        @snapshot="makeSnapshot"
      />
    </div>
    <!-- Timeline controls -->
    <div class="controlsContainer">
      <timeline-controls
        :rewind="rewind"
        :fast-forward="fastForward"
        :play-disabled="playBlocked"
        :time="time"
        :timezone="timezone"
        :rewind-disabled="onLive"
        :fast-forward-disabled="onLive"
        :left-crop-controls-disabled="!isSliderLeftVisible"
        :right-crop-controls-disabled="!isSliderRightVisible"
        @openTimezone="openTimezoneModal"
        @ndvrPause="pause"
        @ndvrPlay="play"
        @ndvrFFStart="startFF"
        @ndvrFFStop="endFF"
        @ndvrRewindStart="startRewind"
        @ndvrRewindStop="endRewind"
        @ndvrSkipToLive="skipToLive"
        @ndvrSkipToStart="skipToStart"
        @changeValueCheckboxThumbnails="
          $emit('valueCheckboxThumbnails', $event)
        "
        @rangeInfoValue="$emit('valueRangeInfo', $event)"
        @moveLeftCrop="$emit('moveLeftCrop', $event)"
        @moveRightCrop="$emit('moveRightCrop', $event)"
        @moveLeftCropToCursor="$emit('moveLeftCropToCursor')"
        @moveRightCropToCursor="$emit('moveRightCropToCursor')"
      />
    </div>
  </div>
</template>

<script>
import "vue-slider-component/theme/default.css";

/** components */
import SegmentPlayer from "@/components/media/video/SegmentPlayer.vue";
import PlayerControls from "@/components/media/controls/PlayerControls.vue";
import TimelineControls from "@/components/media/controls/TimelineControls.vue";

import {
  saveVolume,
  restoreVolume,
  saveMuted,
  restoreMuted
} from "@/api/player.js";

/** vuex */
import { mapState, mapActions, mapGetters } from "vuex";

/** time formatting */
import { getTimeLine } from "@/utils/time.js";

/** shortcuts tools */
import { subscribeToKeydown, subscribeToKeyup } from "@/utils/shortcuts.js";

/** default poster */
import defaultPoster from "@/assets/images/loading_stream.jpg";

// @group Media/Video
// Channel player component
export default {
  name: "NdvrVideoPlayer",
  components: {
    /** player */
    SegmentPlayer,
    /** player controls */
    PlayerControls,
    /** timeline controls */
    TimelineControls
  },
  props: {
    // NDVR stream link
    ndvrStream: {
      type: String,
      default: null
    },
    // Player poster
    poster: {
      type: String,
      default: defaultPoster
    },
    // Player time timestamp
    time: {
      type: Number,
      required: true
    },
    // Play/pause video
    toPlay: {
      type: Boolean,
      default: false,
      required: true
    },
    // Has stream video or not
    hasVideo: {
      type: Boolean,
      default: true
    },

  },
  data() {
    return {
      isStreamInited: false,
      /** HTML video tag */
      player: null,
      /** play/pause function blocked */
      playBlocked: false,
      /** is player loading new segment */
      playerLoading: true,
      /** player sound settings */
      sound: {
        volume: 1.0,
        muted: false
      },
      /** player mode (normal, rewind, fast) */
      playerMode: "normal",
      /** rewind step in seconds */
      rewindStep: 2,
      /** paddings */
      edgePadding: 120,
      /** need to restart player after modal closing */
      temporaryPaused: false,
      /** current time used for 'live' player */
      liveTime: 0,
      /** time to display on player controls */
      displayTime: this.time,
      /** is playing live or DVR */
      onLive: false
    };
  },
  computed: {
    ...mapState({
      /** timeline */
      streamStart: state => state.timeline.ndvrStreamStart,
      streamEnd: state => state.timeline.ndvrStreamEnd,
      streamBreaks: state => state.timeline.breaks,
      /** userData */
      timezone: state => state.timezone.timezone,
      /** modals */
      modals: state => state.modals.clipper.MODALS,
      openedModal: state => state.modals.clipper.opened
    }),
    ...mapGetters("timeline", ["isSliderLeftVisible", "isSliderRightVisible"]),
    timeProp: {
      get() {
        return this.time;
      },
      set(val) {
        this.updateTime(val);
      }
    },
    pausedProp: {
      get() {
        return !this.toPlay;
      },
      set(val) {
        if (val) {
          this.$emit("pause");
        } else {
          this.$emit("play");
        }
      }
    },
    // @vuese
    // Snapshot file name
    snapshotFilename: {
      cache: false,
      get() {
        const timeString = getTimeLine(this.time, this.timezone);
        const filename = `snapshot_${timeString}.png`;
        return filename;
      },
      isStreamInited() {
        return (
          this.ndvrStream && this.time && this.streamStart && this.streamEnd
        );
      }
    },
    // @vuese
    // Is player rewinding
    rewind() {
      return this.playerMode == "rewind";
    },
    // @vuese
    // Is player in fast fowrard mode
    fastForward() {
      return this.playerMode == "fast";
    },
    // @vuese
    // Volume in player
    actualVolume() {
      return this.sound.muted ? 0 : this.sound.volume;
    },
    // @vuese
    // If time in break it's current break interval, otherwise it's null
    currentBreak() {
      for (const { start, end } of this.streamBreaks) {
        if (this.time >= start && this.time <= end) {
          return { start, end };
        }
      }
      return null;
    }
  },
  watch: {
    playerMode() {
      if (this.playerMode == "fast" || this.playerMode == "rewind") {
        this.playBlocked = true;
        this.pausedProp = false;
      } else {
        this.playBlocked = false;
      }
    },
    // @vuese
    // Pause/play when modal opened/closed
    openedModal() {
      if (this.openedModal !== null && this.toPlay) {
        this.pause();
        if (this.openedModal == "calendar") {
          this.temporaryPaused = false;
        } else {
          this.temporaryPaused = true;
        }
      } else if (this.openedModal === null && this.temporaryPaused) {
        this.play();
        this.temporaryPaused = false;
      }
    },
    // @vuese
    // Init stream live
    streamEnd() {
      this.initialize(this.streamEnd);
      this.liveTime = this.streamEnd;
    },
    // @vuese
    // Save volume
    "sound.volume"() {
      saveVolume(this.sound.volume);
    },
    // @vuese
    // Save muted
    "sound.muted"() {
      saveMuted(this.sound.muted);
    },
    // @vuese
    // Move cursor to end or start of break interval
    currentBreak() {
      if (this.currentBreak) {
        if (!this.rewind) {
          this.updateTime(this.currentBreak.end + 1);
        } else {
          this.updateTime(this.currentBreak.start - 1);
        }
      }
    },
    // @vuese
    // Update displaying time
    time() {
      this.displayTime = this.time;
    },
    // @vuese
    // Disable fast forward & rewind on live mode to prevent errors
    onLive() {
      if (this.onLive) {
        if (this.fastForward) {
          this.endFF();
        }

        if (this.rewind) {
          this.endRewind();
        }
      }
    }
  },
  mounted() {
    if (this.ndvrStream) {
      this.isStreamInited = true;
    } else {
      const unwatchStream = this.$watch("ndvrStream", () => {
        if (this.ndvrStream) {
          this.isStreamInited = true;
          unwatchStream();
        }
      });
    }

    /** snapshot shortcuts */
    subscribeToKeydown("KeyS", () => {
      if (this.openedModal == this.modals.SNAPSHOT) {
        this.closeClipperModal();
      } else {
        this.makeSnapshot();
      }
    });
    subscribeToKeyup("KeyM", this.toggleMutePlayer);

    /** volume initializing */
    const volume = restoreVolume();
    const muted = restoreMuted();

    if (muted !== null) {
      this.sound.muted = muted;
    }

    if (!isNaN(volume)) {
      this.sound.volume = volume;
    }

    /** live time updating */
    /** this.liveTime = this.streamEnd; */
    setInterval(() => {
      this.liveTime += 1;
    }, 1000);
  },
  methods: {
    ...mapActions("ndvrPlayer", ["initialize"]),
    ...mapActions("modals", ["openClipperModal", "closeClipperModal"]),
    // @vuese
    // Disables/enables player sound
    toggleMutePlayer() {
      this.sound.muted = !this.sound.muted;
    },
    // @vuese
    // Start player
    play() {
      if (!this.playBlocked) {
        this.$emit("play");
      }
    },
    // @vuese
    // Stop player
    pause() {
      if (!this.playBlocked) {
        this.$emit("pause");
      }
    },
    // @vuese
    // Set sound volume level
    // @arg volume - volume level in range [0-1]
    setVolume(volume) {
      const boundedVolume = Math.min(Math.max(volume, 0), 1);
      this.sound.muted = boundedVolume == 0;
      this.sound.volume = boundedVolume;
    },
    // @vuese
    // Go to current stream video (in real time)
    skipToLive() {
      this.$emit("updateTime", this.streamEnd);
    },
    // @vuese
    // Go to first accessible stream video
    skipToStart() {
      this.$emit("updateTime", this.streamStart + this.edgePadding);
    },
    // @vuese
    // Set double video speed
    startFF() {
      if (!this.onLive) {
        this.playerMode = "fast";
      }
    },
    // @vuese
    // Set normal video speed
    endFF() {
      this.playerMode = "normal";
    },
    // @vuese
    // Start to play video in reverse direction (2 sec / 1 sec)
    startRewind() {
      if (!this.onLive) {
        this.playerMode = "rewind";
      }
    },
    // @vuese
    // Stop to play video in reverse direction (2 sec / 1 sec)
    endRewind() {
      this.playerMode = "normal";
    },
    // @vuese
    // Set new time
    // @arg time - new time
    updateTime(time) {
      /** display time may be incorrect */
      this.displayTime = time;

      time = Math.max(time, this.streamStart);
      time = Math.min(time, this.streamEnd);

      if (this.rewind && time < this.streamStart + this.rewindStep * 3) {
        this.endRewind();
      }

      /** if (this.fastForward && time > this.streamEnd - this.liveTreshold) {
        this.endFF();
      } */

      this.$emit("updateTime", time);
    },
    // @vuese
    // Stop video and opens snapshot panel
    makeSnapshot() {
      if (this.hasVideo && !this.playerLoading) {
        this.openClipperModal({
          modal: this.modals.SNAPSHOT,
          data: {
            player: this.player,
            filename: this.snapshotFilename
          }
        });
      }
    },
    // @vuese
    // Open temesone select modal
    openTimezoneModal() {
      this.openClipperModal({
        modal: this.modals.TIMEZONE,
        data: null
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.dvrPlayer {
  height: 95%;

  display: flex;
  flex-direction: column;
}

.playerContainer {
  flex-grow: 1;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--grey);

  &__fallback {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

.fallback-poster {
  display: flex;
  background-color: black;
  align-items: center;

  &__image {
    width: 100%;
  }
}
//тек
.videoPlayerTag {
  width: 100%;
  height: 100% !important;
  background-color: black;
}

.player-controls {
  opacity: 0;
  transition: opacity 0.5s;
  z-index: var(--player-controls-z);

  &_visible_always {
    opacity: 1;
  }
}

.dvrPlayer:hover .player-controls {
  opacity: 1;
}

/** timeline controls */
.controlsContainer {
  margin-top: -10px;
  background: #233d4e;
  z-index: 1111;
  border-bottom: solid 1px var(--light-grey);
}

@media only screen and (max-width: 767px) {
  .controlsContainer {
    margin-top: -10px;
    margin-top: 5px;
    margin-bottom: 10px;
    border-bottom: none;
  }
}

@media only screen and (max-width: 1238px) {
  .controlsContainer {
    margin-top: -10px;
    border-bottom: none;
  }
}

@media only screen and (max-width: 1900px) {
  .controlsContainer {
    margin-top: 0px;
    border-bottom: none;
  }
}
</style>
